import { AssetTypeId, System2d, SystemNumberOrientation } from '@damofire/data-watson';
import { CanvasDisplayPrecision, CanvasRenderer, CanvasRenderingCtx } from '@damofire/uix-commons';
import {
  BAY_CAPACITY_COLOR_CODES,
  BAY_STROKE_WIDTH,
  DEFAULT_BAY_CFG,
  DEFAULT_BAY_STROKE_CFG,
  DEFAULT_BAY_TEXT_CFG,
  DEFAULT_CANTILEVER_BAY_CFG,
  DEFAULT_CAPACITY_TEXT_CFG,
  NOTE_BAY_ICON_CFG,
  UPRIGHT_INSPECTION_STATUS_COLOR_CODES,
} from './canvas.cfg';
import { CapacityClass, LayoutBay } from './layout-components';

function getMostCommonFillColor(layoutBays: LayoutBay[]): string {
  if (layoutBays.length === 0) {
    return UPRIGHT_INSPECTION_STATUS_COLOR_CODES.incomplete;
  }

  const fillColorCount: { [key: string]: number } = {};

  layoutBays.forEach((bay) => {
    const color: string = typeof bay.fill === 'string' ? bay.fill : UPRIGHT_INSPECTION_STATUS_COLOR_CODES.complete;
    if (fillColorCount[color]) {
      fillColorCount[color]++;
    } else {
      fillColorCount[color] = 1;
    }
  });

  let mostCommonFill = typeof layoutBays[0].fill === 'string' ? layoutBays[0].fill : UPRIGHT_INSPECTION_STATUS_COLOR_CODES.incomplete;
  let maxCount = 0;

  for (const fill in fillColorCount) {
    if (fillColorCount[fill] > maxCount) {
      mostCommonFill = fill;
      maxCount = fillColorCount[fill];
    }
  }

  return mostCommonFill;
}

export class SystemLayoutRenderer extends CanvasRenderer {
  selectedAsset: string;
  fill: string | CanvasPattern;

  constructor(
    private readonly ctx: CanvasRenderingContext2D,
    readonly system: System2d,
    readonly layoutBays: LayoutBay[],
    private readonly angleRad: number,
    private _displayCapacities: boolean,
    private readonly fullWidth: number,
    private readonly height: number,
    private readonly averageCapacityClass: CapacityClass,
    private readonly showSystemNumbers: boolean = true,
    private readonly systemNumberOrientation: SystemNumberOrientation = SystemNumberOrientation.DOWN_AISLE,
  ) {
    super();
  }

  draw(renderingCtx: CanvasRenderingCtx): void {
    if (renderingCtx.precision === CanvasDisplayPrecision.FULL) {
      const drawAfter = this.layoutBays.reduce(
        (p, b, inx) => {
          if ((inx === this.layoutBays.length - 1 && this.selectedAsset === b.nextUpright.assetId) || b.upright.assetId === this.selectedAsset) {
            p.push({ b, inx });
          } else {
            this.drawBay(renderingCtx, b, inx);
          }
          return p;
        },
        [] as { b: LayoutBay; inx: number }[],
      );

      if (drawAfter.length) {
        drawAfter.forEach((b) => this.drawBay(renderingCtx, b.b, b.inx, true));
      }
    } else {
      this.drawSystem();
    }
  }

  private drawSystem(): void {
    // Checkpoint
    this.ctx.save();
    this.ctx.beginPath();

    const firstB = this.layoutBays[0];
    if (firstB) {
      this.ctx.translate(firstB.x, firstB.y);
    } else {
      console.error('Missing first bay of system', this.system);
    }
    this.ctx.rotate(this.angleRad);

    this.ctx.rect(0, 0, this.fullWidth, this.height);

    this.ctx.lineWidth = DEFAULT_BAY_CFG.strokeWidth;
    this.ctx.strokeStyle = DEFAULT_BAY_CFG.stroke;
    this.ctx.stroke();

    if (!this.fill) {
      this.ctx.fillStyle = !this._displayCapacities ? DEFAULT_BAY_CFG.fill : BAY_CAPACITY_COLOR_CODES[this.averageCapacityClass];
    } else {
      this.ctx.fillStyle = getMostCommonFillColor(this.layoutBays);
    }

    this.ctx.fill();

    this.ctx.restore();
  }

  set displayCapacities(display: boolean) {
    this._displayCapacities = display;
    if (this.layoutBays) {
      this.layoutBays.forEach((b) => {
        if (display) {
          b.fill = BAY_CAPACITY_COLOR_CODES[b.capacityClass];
        } else {
          b.fill = DEFAULT_BAY_CFG.fill;
        }
      });
    }
  }

  private drawBay(renderingCtx: CanvasRenderingCtx, b: LayoutBay, inx: number, selected = false): void {
    // Checkpoint
    this.ctx.save();
    this.ctx.beginPath();

    this.ctx.translate(b.x, b.y);
    this.ctx.rotate(this.angleRad);

    this.drawBayShape(b, selected);

    if (renderingCtx.precision === CanvasDisplayPrecision.FULL) {
      this.drawBayAnnotations(b, inx);
    }

    this.ctx.restore();
  }

  private drawBayShape(b: LayoutBay, selected: boolean): void {
    if (b.upright.assetTypeId === AssetTypeId.CANTILEVER) {
      this.ctx.lineWidth = DEFAULT_CANTILEVER_BAY_CFG.strokeWidth;
      this.ctx.beginPath();
      this.ctx.moveTo(0, 0);
      this.ctx.lineTo(0, b.h);
      this.ctx.lineTo(b.w, b.h);
      this.ctx.lineTo(b.w, 0);
    } else {
      this.ctx.rect(0, 0, b.w, b.h);
      this.ctx.lineWidth = DEFAULT_BAY_CFG.strokeWidth;
    }

    this.ctx.strokeStyle = DEFAULT_BAY_CFG.stroke;
    this.ctx.stroke();

    if (selected) {
      this.ctx.lineWidth = BAY_STROKE_WIDTH;
      this.ctx.strokeStyle = DEFAULT_BAY_STROKE_CFG.fill;
      this.ctx.stroke();
    }

    this.ctx.fillStyle = b.fill;
    this.ctx.fill();

    if (selected) {
      this.ctx.fillStyle = DEFAULT_BAY_STROKE_CFG.fill;
      this.ctx.fill();
    }
  }

  private drawBayAnnotations(b: LayoutBay, inx: number): void {
    this.ctx.fillStyle = 'black';
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';

    if (inx === 0 && this.showSystemNumbers) {
      this.drawSystemNumberLabel(b);
    }

    if (this._displayCapacities) {
      this.drawCapacityLabel(b);
    } else if (b.hasNotes) {
      this.drawNoteIcon(b);
    }
  }

  private drawSystemNumberLabel(b: LayoutBay): void {
    this.ctx.save();
    this.ctx.font = `${DEFAULT_BAY_TEXT_CFG.fontWeight} ${DEFAULT_BAY_TEXT_CFG.fontSize}px ${DEFAULT_BAY_TEXT_CFG.fontFamily}`;
    this.ctx.translate(b.w / 2, -DEFAULT_BAY_TEXT_CFG.fontSize);

    if (this.systemNumberOrientation === SystemNumberOrientation.ALWAYS_UP) {
      this.ctx.rotate(-this.angleRad);
    }

    this.ctx.scale(1, -1);

    if (b.aisle) {
      this.ctx.fillText(`${b.system} / ${b.aisle}`, 0, 0);
    } else {
      this.ctx.fillText(b.system, 0, 0);
    }

    this.ctx.restore();
  }

  private drawCapacityLabel(b: LayoutBay): void {
    this.ctx.save();
    this.ctx.font = `${DEFAULT_CAPACITY_TEXT_CFG.fontWeight} ${DEFAULT_CAPACITY_TEXT_CFG.fontSize}px ${DEFAULT_CAPACITY_TEXT_CFG.fontFamily}`;
    this.ctx.translate(b.w / 2, b.h / 2);
    this.ctx.scale(1, -1);

    this.ctx.fillText(`${b.capacity ?? 0}`, 0, 0);
    this.ctx.restore();
  }

  private drawNoteIcon(b: LayoutBay): void {
    this.ctx.translate(b.w / 2, b.h / 2);
    this.ctx.rotate(-this.angleRad);
    this.ctx.scale(1, -1);
    this.ctx.fillStyle = NOTE_BAY_ICON_CFG.fill;
    this.ctx.font = `${NOTE_BAY_ICON_CFG.fontSize}px ${NOTE_BAY_ICON_CFG.fontFamily}`;
    this.ctx.fillText('description', 0, 0);
  }
}
